import type { Action, Thunk } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { StoreModel } from '../../';
import parseStatusCode from '../../errors/parseStatusCode';
import type { Injections } from '../../services/injections';
import type { ApplicationErrorOptions } from '../../../types';
import type { PartnerAddressUpsert, PartnerAddressV2 } from '@shipium-corp/shipium-ts-types';

export interface AddressesModel {
  addressesMap: Record<string, PartnerAddressV2>;
  setAddresses: Action<AddressesModel, PartnerAddressV2[]>;
  setAddress: Action<AddressesModel, PartnerAddressV2>;
  fetchAddressesLoading: boolean;
  setFetchAddressesLoading: Action<AddressesModel, boolean>;
  createAddress: Thunk<AddressesModel, PartnerAddressUpsert, Injections, StoreModel, Promise<void>>;
  editAddress: Thunk<
    AddressesModel,
    PartnerAddressUpsert & { addressId: string },
    Injections,
    StoreModel,
    Promise<void>
  >;
  deleteAddress: Thunk<AddressesModel, string, Injections, StoreModel, Promise<PartnerAddressV2>>;
  deleteAddresses: Thunk<
    AddressesModel,
    string[],
    Injections,
    StoreModel,
    Promise<PromiseSettledResult<PartnerAddressV2>[]>
  >;
  fetchAddresses: Thunk<AddressesModel, void, Injections, StoreModel, Promise<void>>;
  fetchAddressesFailure: Action<AddressesModel, ApplicationErrorOptions>;
}

export const addressesModel: AddressesModel = {
  addressesMap: {},
  setAddresses: action((state, addresses) => {
    const addressMap = addresses.reduce<Record<string, PartnerAddressV2>>((acc, curr) => {
      acc[curr.addressId!] = curr;
      return acc;
    }, {});
    state.addressesMap = addressMap;
  }),
  setAddress: action((state, address) => {
    state.addressesMap[address.addressId!] = address;
  }),
  fetchAddressesLoading: false,
  setFetchAddressesLoading: action((state, isLoading) => {
    state.fetchAddressesLoading = isLoading;
  }),
  fetchAddresses: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.setFetchAddressesLoading(true);
      const { fulfillmentContextService } = injections;
      const response = await fulfillmentContextService.fetchAddresses();
      actions.setAddresses(response);
      actions.setFetchAddressesLoading(false);
      return response;
    } catch (error) {
      actions.fetchAddressesFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch asset details',
      });
      actions.setFetchAddressesLoading(false);
      return error;
    }
  }),
  createAddress: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.setFetchAddressesLoading(true);
      const { fulfillmentContextService } = injections;
      // any typing to avoid conflict with legacy hand rolled types
      const response = await fulfillmentContextService.createAddress(payload as any);
      actions.setAddresses([...Object.values(getState().addressesMap), response]);
      actions.setFetchAddressesLoading(false);
      return response;
    } catch (error) {
      actions.fetchAddressesFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch asset details',
      });
      actions.setFetchAddressesLoading(false);
      return error;
    }
  }),
  editAddress: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.setFetchAddressesLoading(true);
      const { fulfillmentContextService } = injections;
      // any typing to avoid conflict with legacy hand rolled types
      const response = await fulfillmentContextService.updateAddress(payload as any);
      actions.setAddresses(
        Object.values(getState().addressesMap).map((address) =>
          address.addressId === payload.addressId ? response : address,
        ),
      );
      actions.setFetchAddressesLoading(false);
      return response;
    } catch (error) {
      actions.fetchAddressesFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch asset details',
      });
      actions.setFetchAddressesLoading(false);
      return error;
    }
  }),

  deleteAddress: thunk(async (actions, addressId, { injections, getState }) => {
    try {
      actions.setFetchAddressesLoading(true);
      const { fulfillmentContextService } = injections;
      // Archive via DELETE. The backend gate-keeps: it returns 409 (and does NOT archive)
      // if the address is still linked to any other resource. On success it is archived,
      // so drop it from the list.
      const response = await fulfillmentContextService.deleteAddress(addressId);
      actions.setAddresses(
        Object.values(getState().addressesMap).filter((existing) => existing.addressId !== addressId),
      );
      actions.setFetchAddressesLoading(false);
      return response;
    } catch (error) {
      actions.setFetchAddressesLoading(false);
      // Re-throw so the caller can surface the conflict/failure (e.g. a 409 "still linked").
      throw error;
    }
  }),
  deleteAddresses: thunk(async (actions, addressIds, { injections, getState }) => {
    // Bulk archive: own the loading boundary once for the whole batch (rather than per-item,
    // which would clear the flag the moment the first concurrent delete settles) and update the
    // list a single time. Returns the settled results so the caller can report per-address
    // outcomes (e.g. 409 "still linked" vs. other failures).
    actions.setFetchAddressesLoading(true);
    try {
      const { fulfillmentContextService } = injections;
      const results = await Promise.allSettled(
        addressIds.map((addressId) => fulfillmentContextService.deleteAddress(addressId)),
      );
      const deletedIds = new Set(addressIds.filter((_, index) => results[index].status === 'fulfilled'));
      if (deletedIds.size > 0) {
        actions.setAddresses(
          Object.values(getState().addressesMap).filter((existing) => !deletedIds.has(existing.addressId!)),
        );
      }
      return results;
    } finally {
      actions.setFetchAddressesLoading(false);
    }
  }),
  fetchAddressesFailure: action((state, error) => {
    console.error(error);
  }),
};

export default addressesModel;
